import React from "react";
import { Link } from "react-router-dom";
import { Mail, ShieldCheck, FileText, Users, ArrowLeft } from "lucide-react";

function LegalShell({ title, icon: Icon, updated, children, testid }) {
  return (
    <div className="min-h-screen bg-slate-50" data-testid={testid}>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/branding/zanitel_logo.png" alt="Zeeptel" className="h-8 w-auto" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>
        </div>
        {updated && <p className="text-xs text-slate-400 mb-8">Last updated: {updated}</p>}
        <div className="prose-sm text-slate-600 space-y-4 leading-relaxed [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:text-lg [&_h2]:mt-8 [&_h2]:mb-2">
          {children}
        </div>
      </main>
      <footer className="border-t border-slate-200 bg-white py-6 text-center text-xs text-slate-400">
        © 2026 Zanitel LLC · <a href="https://zeeptel.com" className="text-[#2563EB]">zeeptel.com</a> · connecting the world ·{" "}
        <Link to="/privacy" className="hover:underline">Privacy</Link> · <Link to="/terms" className="hover:underline">Terms</Link> ·{" "}
        <Link to="/contact" className="hover:underline">Contact</Link>
      </footer>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" icon={ShieldCheck} updated="July 2026" testid="privacy-page">
      <p>ZEEPTEL is operated by <b>Zanitel LLC</b>, a Minnesota limited liability company ("we", "us"), located at 3 14th Avenue NE, St. Cloud, MN 56304, USA. We operate zeeptel.com, a prepaid international calling and travel eSIM service. This policy explains what we collect and how we use it.</p>
      <h2>Information we collect</h2>
      <p>Account details (name, email, phone number), wallet transactions, call records (destination number, duration, cost — never the audio content of your calls), eSIM orders, contacts you choose to save, and support conversations.</p>
      <h2>How we use it</h2>
      <p>To connect your calls, bill accurately per minute, deliver eSIM QR codes, send service emails and SMS (receipts, balance alerts, call follow-ups), prevent fraud, and improve the service. We never sell your personal data.</p>
      <h2>Payments</h2>
      <p>Card payments are processed by Stripe. We never see or store your full card number. Calls and SMS are carried by Twilio; emails are delivered by SendGrid.</p>
      <h2>Data retention &amp; your rights</h2>
      <p>We keep account and transaction records as required for billing and legal compliance. You can request a copy or deletion of your data at any time by emailing <a href="mailto:support@zeeptel.com">support@zeeptel.com</a>.</p>
      <h2>Cookies</h2>
      <p>We use a single secure, httpOnly session cookie to keep you logged in. No advertising or cross-site tracking cookies.</p>
      <h2>Contact</h2>
      <p>Privacy questions: <a href="mailto:support@zeeptel.com">support@zeeptel.com</a>.</p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service" icon={FileText} updated="July 2026" testid="terms-page">
      <p>By creating a ZEEPTEL account you agree to these terms. The service is operated by <b>Zanitel LLC</b>, a Minnesota limited liability company located at 3 14th Avenue NE, St. Cloud, MN 56304, USA. These terms are governed by the laws of the State of Minnesota.</p>
      <h2>The service</h2>
      <p>ZEEPTEL provides prepaid international calling using a callback bridge (we ring your phone, then connect you to your destination) and travel eSIM data plans. Standard incoming-call charges from your own mobile operator may apply to the leg we place to your phone.</p>
      <h2>Wallet &amp; billing</h2>
      <p>Calls are billed per minute at the rate shown before you dial, deducted from your prepaid wallet. Your credit does not expire. Failed or unanswered calls are not charged. eSIM purchases are charged at the price shown at checkout and are non-refundable once the QR code has been issued, except where the eSIM fails to activate.</p>
      <h2>Refunds</h2>
      <p>Unused wallet balance refunds are handled case by case — contact <a href="mailto:billing@zeeptel.com">billing@zeeptel.com</a>. Charges you believe are incorrect must be reported within 30 days.</p>
      <h2>Acceptable use</h2>
      <p>You may not use ZEEPTEL for unlawful purposes, harassment, fraud, auto-dialing/robocalls, or resale without our written consent. We may suspend accounts that show fraudulent or abusive activity.</p>
      <h2>Service quality</h2>
      <p>Call quality depends partly on destination networks beyond our control. The service is provided "as is"; our liability is limited to the balance in your wallet.</p>
      <h2>Changes</h2>
      <p>We may update these terms; continued use after changes constitutes acceptance. Material changes will be announced by email.</p>
      <h2>Contact</h2>
      <p><a href="mailto:support@zeeptel.com">support@zeeptel.com</a></p>
    </LegalShell>
  );
}

export function About() {
  return (
    <LegalShell title="About ZEEPTEL" icon={Users} testid="about-page">
      <p><b>ZEEPTEL — connecting the world.</b> We help the African diaspora and global travelers stay close to the people who matter, affordably. ZEEPTEL is a service of <b>Zanitel LLC</b>, a registered US company based in St. Cloud, Minnesota.</p>
      <h2>What we do</h2>
      <p>Our callback-bridge technology lets you call any mobile or landline in 185+ countries — the person you call needs no internet, no app, and no smartphone. It works even on 2G networks in rural areas. We also offer instant travel eSIMs for 16+ destinations so you land with data already working.</p>
      <h2>Why we exist</h2>
      <p>Calling home shouldn't cost more than the groceries you're calling about. We believe in transparent per-minute pricing shown before you dial, prepaid credit that never expires, and no contracts or hidden fees. Let's bridge the communication. Let us build the world.</p>
      <h2>Talk to us</h2>
      <p>We read every message: <a href="mailto:support@zeeptel.com">support@zeeptel.com</a>, or use the Zeep assistant on any page.</p>
    </LegalShell>
  );
}

export function Contact() {
  return (
    <LegalShell title="Contact Us" icon={Mail} testid="contact-page">
      <p>We're here to help, and we answer fast.</p>
      <h2>Support</h2>
      <p>Account, calling, or eSIM help: <a href="mailto:support@zeeptel.com"><b>support@zeeptel.com</b></a> — or chat with <b>Zeep</b>, our assistant, via the chat bubble on any page (it can escalate you to a human).</p>
      <h2>Billing</h2>
      <p>Payments, receipts, and refunds: <a href="mailto:billing@zeeptel.com"><b>billing@zeeptel.com</b></a></p>
      <h2>Partnerships &amp; media</h2>
      <p><a href="mailto:marketing@zeeptel.com"><b>marketing@zeeptel.com</b></a></p>
      <h2>Feedback</h2>
      <p>Tell us how to make your experience better using the red feedback button at the bottom-left of every page — every message is read by the team.</p>
    </LegalShell>
  );
}
